// L3 — timeline and animation (CREATIVE-MODEL-V1.md §7). Represents and
// validates; nothing is played. Presets are provenance only: the keyframes
// in the document are the canonical animation (see presets.ts).

import type { AnimatableProps, AnimationTrack, Scene } from "../schema/document.js";
import type { IssueSink } from "../issues.js";
import { rootOf, type DocIndex } from "./context.js";

function checkTrack(track: AnimationTrack, path: string, scene: Scene | undefined, sink: IssueSink) {
  const kfs = track.keyframes;
  const first = kfs[0];
  const last = kfs[kfs.length - 1];
  if (first?.at !== 0 || last?.at !== 1)
    sink.add("KEYFRAMES_INVALID", `${path}.keyframes`, "Keyframes must start at 0 and end at 1.", {});
  for (let i = 1; i < kfs.length; i++) {
    const prev = kfs[i - 1];
    const cur = kfs[i];
    if (prev && cur && cur.at <= prev.at)
      sink.add(
        "KEYFRAMES_INVALID",
        `${path}.keyframes[${String(i)}].at`,
        "Keyframe offsets must be strictly increasing.",
        {}
      );
  }
  if (kfs.every((k) => Object.keys(k.props).length === 0))
    sink.add("KEYFRAMES_INVALID", `${path}.keyframes`, "At least one keyframe must animate a property.", {});
  if (track.iterations === "infinite" && track.phase !== "emphasis" && track.phase !== "custom")
    sink.add("INFINITE_NOT_ALLOWED", `${path}.iterations`, "Only emphasis and custom tracks may repeat indefinitely.", {
      phase: track.phase,
    });
  if (track.start.mode === "timeline" && scene && scene.duration !== null && track.iterations !== "infinite") {
    const end = track.start.at + (track.delay ?? 0) + track.duration * (track.iterations ?? 1);
    if (end > scene.duration)
      sink.add(
        "TIMING_INVALID",
        path,
        `Track ends at ${String(end)} ms, after the scene (${String(scene.duration)} ms).`,
        {
          end,
          sceneDuration: scene.duration,
        }
      );
  }
}

interface Window {
  readonly start: number;
  readonly end: number;
  readonly props: ReadonlySet<keyof AnimatableProps>;
  readonly id: string;
}

function windows(tracks: readonly AnimationTrack[]): Window[] {
  return tracks
    .filter((t) => t.start.mode === "timeline")
    .map((t) => {
      const start = (t.start.mode === "timeline" ? t.start.at : 0) + (t.delay ?? 0);
      const end = t.iterations === "infinite" ? Number.POSITIVE_INFINITY : start + t.duration * (t.iterations ?? 1);
      const props = new Set<keyof AnimatableProps>();
      for (const k of t.keyframes) for (const p of Object.keys(k.props)) props.add(p as keyof AnimatableProps);
      return { start, end, props, id: t.id };
    });
}

function checkOverlaps(tracks: readonly AnimationTrack[], path: string, sink: IssueSink) {
  const ws = windows(tracks);
  for (let i = 0; i < ws.length; i++)
    for (let j = i + 1; j < ws.length; j++) {
      const a = ws[i];
      const b = ws[j];
      if (!a || !b || a.start >= b.end || b.start >= a.end) continue;
      const shared = [...a.props].filter((p) => b.props.has(p));
      if (shared.length > 0)
        sink.warn(
          "ANIMATION_OVERLAP",
          path,
          `Tracks "${a.id}" and "${b.id}" animate ${shared.join(", ")} at the same time; the later track wins.`,
          {
            tracks: `${a.id}|${b.id}`,
          }
        );
    }
}

export function validateAnimation(index: DocIndex, sink: IssueSink): void {
  const doc = index.doc;
  doc.scenes.forEach((scene, si) => {
    const sp = `$.scenes[${String(si)}]`;
    if (scene.duration === null && scene.onTimelineEnd !== undefined && scene.onTimelineEnd !== "hold")
      sink.warn(
        "TIMING_INVALID",
        `${sp}.onTimelineEnd`,
        "Interactive scenes (no duration) never reach a timeline end.",
        {}
      );
  });
  doc.elements.forEach((el, ei) => {
    const p = `$.elements[${String(ei)}]`;
    const root = rootOf(index, el.id);
    const scene = root && root !== "overlay" ? index.scenes.get(root)?.value : undefined;
    if (el.timing?.start !== undefined && el.timing.end !== undefined && el.timing.end <= el.timing.start)
      sink.add("TIMING_INVALID", `${p}.timing`, "Element end must be after its start.", {});
    if (scene && scene.duration !== null && (el.timing?.end ?? 0) > scene.duration)
      sink.add("TIMING_INVALID", `${p}.timing.end`, "Element ends after its scene.", { sceneDuration: scene.duration });
    el.animations?.forEach((t, ti) => {
      checkTrack(t, `${p}.animations[${String(ti)}]`, scene, sink);
    });
    if (el.animations) checkOverlaps(el.animations, `${p}.animations`, sink);
    for (const [layoutId, override] of doc.layouts.flatMap((l) =>
      l.overrides && Object.hasOwn(l.overrides, el.id) ? [[l.id, l.overrides[el.id]] as const] : []
    )) {
      const li = doc.layouts.findIndex((l) => l.id === layoutId);
      override?.animations?.forEach((t, ti) => {
        checkTrack(t, `$.layouts[${String(li)}].overrides.${el.id}.animations[${String(ti)}]`, scene, sink);
      });
    }
  });
}
